import { ProcessingStatus } from '@/hooks/useKeyValOperations';
import type { BlogLatestActivity, BlogWithLatestActivity } from '@/services/DataService';
import type { KeywordRowWithStatus } from '@/components/content-outline/types';
import type { BlogPostRowWithStatus } from '@/components/content-refresh/types';

/**
 * Reads a keyword list off a SOR blog's metadata.
 *
 * @param metadata - The blog's metadata.
 * @param field - The metadata field holding the list.
 *
 * @returns The keywords, or an empty list.
 */
const keywordList = (metadata: Record<string, unknown> | undefined, field: string): string[] => {
  const value = metadata?.[field];
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
};

/**
 * Maps a blog's latest outline activity onto the old chat queue's statuses,
 * including the draft states the old Output screen showed.
 *
 * @param activity - The blog's latest activity.
 *
 * @returns The queue status.
 */
export function outlineQueueStatus (activity: BlogLatestActivity | null): ProcessingStatus {
  switch (activity?.status) {
    case 'GENERATING_DRAFT':
      return ProcessingStatus.IN_PROGRESS;
    case 'WAITING_FOR_APPROVAL':
      return ProcessingStatus.WAITING_FOR_APPROVAL;
    case 'SUCCESS':
      return activity.draft_in_progress ? ProcessingStatus.DRAFT_GENERATING : ProcessingStatus.COMPLETED;
    case 'DRAFT_GENERATED':
      return activity.draft_in_progress ? ProcessingStatus.DRAFT_GENERATING : ProcessingStatus.DRAFT_GENERATED;
    case 'FAILED':
      return ProcessingStatus.FAILED;
    default:
      return ProcessingStatus.PENDING;
  }
}

/**
 * Maps a blog's latest refresh activity onto the old chat queue's statuses.
 *
 * @param activity - The blog's latest activity.
 *
 * @returns The queue status.
 */
export function refreshQueueStatus (activity: BlogLatestActivity | null): ProcessingStatus {
  switch (activity?.status) {
    case 'IN_PROGRESS':
      return ProcessingStatus.IN_PROGRESS;
    case 'WAITING_FOR_APPROVAL':
      return ProcessingStatus.WAITING_FOR_APPROVAL;
    case 'SUCCESS':
      return ProcessingStatus.COMPLETED;
    case 'FAILED':
      return ProcessingStatus.FAILED;
    default:
      return ProcessingStatus.PENDING;
  }
}

/**
 * Builds the outline queue table's row for a SOR blog.
 *
 * @param blog - The blog with its latest activity.
 *
 * @returns The table row.
 */
export function toOutlineQueueRow (blog: BlogWithLatestActivity): KeywordRowWithStatus {
  return {
    _id: blog.id,
    _status: outlineQueueStatus(blog.latest_activity),
    key: blog.key,
    created_at: blog.latest_activity?.created_at ?? blog.created_at,
    updated_at: blog.updated_at,
    data: {
      primary_keywords: keywordList(blog.metadata, 'primary_keywords'),
      secondary_keywords: keywordList(blog.metadata, 'secondary_keywords'),
      workflow_execution_id: blog.latest_activity?.workflow_execution_id ?? undefined
    }
  };
}

/**
 * Builds the refresh queue table's row for a SOR blog.
 *
 * @param blog - The blog with its latest activity.
 *
 * @returns The table row.
 */
export function toRefreshQueueRow (blog: BlogWithLatestActivity): BlogPostRowWithStatus {
  return {
    _id: blog.id,
    _status: refreshQueueStatus(blog.latest_activity),
    key: blog.key,
    created_at: blog.latest_activity?.created_at ?? blog.created_at,
    updated_at: blog.updated_at,
    data: {
      blog_post_url: blog.url ?? '',
      primary_keywords: keywordList(blog.metadata, 'primary_keywords').join(', '),
      workflow_execution_id: blog.latest_activity?.workflow_execution_id ?? undefined
    }
  };
}
